import type { Locale } from "./types.js";

/** Textos del widget. El tipo `Messages` obliga a que `en` tenga exactamente las mismas claves que `es`. */
const es = {
  loading: "Cargando el mapa de butacas…",
  loadError: "No hemos podido cargar el mapa de butacas. Revisa tu conexión e inténtalo de nuevo.",
  authError: "No se ha podido autorizar el mapa de butacas. Avisa al organizador del evento.",
  notFoundError: "No hemos encontrado este evento.",
  retry: "Reintentar",
  map: "Mapa de butacas",
  mapHelp: "Usa las flechas para moverte entre butacas, Intro o Espacio para elegir, y los signos más y menos para acercar o alejar.",
  categories: "Categorías y precios",
  highlight: (label: string) => `Resaltar ${label} en el mapa`,
  legendFree: "Disponible",
  legendMine: "Tu selección",
  legendSold: "Ocupada",
  legendHeld: "En proceso de compra",
  legendBlocked: "No disponible",
  legendAccessible: "Accesible",
  legendTitle: "Leyenda",
  stFree: "disponible",
  stMine: "seleccionada",
  stSold: "ocupada",
  stHeld: "reservada temporalmente por otra persona",
  stBlocked: "no disponible",
  accessible: "accesible",
  seatTip: (section: string, row: string, seat: string) => `${section} · Fila ${row} · Butaca ${seat}`,
  seatAria: (section: string, row: string, seat: string, rest: string[]) =>
    [section, `fila ${row}`, `butaca ${seat}`, ...rest].filter(Boolean).join(", "),
  zoneFree: (n: number) => (n === 1 ? "1 libre" : `${n} libres`),
  zoneSoldOut: "Agotado",
  zoneAria: (label: string, cat: string, price: string, free: number, mine: number) =>
    [label, cat, price && `${price} por entrada`, free > 0 ? `quedan ${free}` : "agotado", mine > 0 ? `${mine} seleccionadas` : ""]
      .filter(Boolean)
      .join(", "),
  zoneEach: "por entrada",
  qty: "Cantidad",
  qtyDec: "Menos",
  qtyInc: "Más",
  zoneAdd: (n: number) => (n === 1 ? "Añadir 1 entrada" : `Añadir ${n} entradas`),
  zoneUpdate: (n: number) => (n === 1 ? "Actualizar a 1 entrada" : `Actualizar a ${n} entradas`),
  zoneRemove: "Quitar de mi selección",
  close: "Cerrar",
  zoneLimit: (n: number) => `Máximo ${n} en esta zona`,
  zoomIn: "Acercar",
  zoomOut: "Alejar",
  zoomReset: "Restablecer vista",
  hintWheel: (mod: string) => `Usa ${mod} + rueda para hacer zoom`,
  hintTouch: "Toca dos veces o pellizca para acercar",
  barEmpty: (max: number) => `Elige tus butacas en el mapa (hasta ${max}).`,
  tickets: (n: number) => (n === 1 ? "1 entrada" : `${n} entradas`),
  removeChip: (label: string) => `Quitar ${label}`,
  holdTime: "Reservadas durante",
  extend: "Extender",
  extending: "Extendiendo…",
  syncing: "Reservando…",
  minuteLeft: "Te queda menos de un minuto para completar la compra.",
  extended: (clock: string) => `Hemos ampliado tu tiempo. Te quedan ${clock}.`,
  extendMax: "Ya no se puede ampliar más el tiempo. Completa la compra ahora.",
  extendFailed: "No hemos podido extender el tiempo. Inténtalo de nuevo.",
  holdExpired: "Tu reserva temporal ha vencido y las butacas se han liberado. Vuelve a elegirlas.",
  holdBooked: "Tu compra se ha confirmado.",
  maxReached: (n: number) => (n === 1 ? "Solo puedes elegir 1 entrada." : `Solo puedes elegir hasta ${n} entradas por compra.`),
  seatLost: (label: string) => `${label} ya no está disponible y la hemos quitado de tu selección.`,
  seatsLost: (n: number) => `${n} butacas ya no están disponibles y las hemos quitado de tu selección.`,
  capacityLost: (zone: string) => `Ya no quedan tantas entradas en ${zone}. Hemos ajustado tu selección.`,
  saveError: "No hemos podido reservar tu selección. Inténtalo de nuevo.",
  removeError: "No hemos podido quitar la butaca. Se mantiene reservada.",
  zonesLimit: "No puedes combinar tantas zonas de entrada general en una misma compra.",
  quotaSession: (n: number) => `Has alcanzado el máximo de ${n === 1 ? "1 butaca reservada" : n > 1 ? `${n} butacas reservadas` : "butacas reservadas"} a la vez. Completa la compra o suelta alguna para continuar.`,
  quotaIp: (n: number) => `Hay demasiadas butacas reservadas desde tu conexión a la vez (máximo ${n}). Suelta alguna o inténtalo de nuevo cuando venzan las reservas.`,
  recovered: "Hemos recuperado tu selección.",
  sessionRequired: "Esta venta exige iniciar sesión en el portal.",
  sessionInvalid: "Tu sesión de compra ha caducado. Recarga la página para continuar.",
  netLost: "Sin conexión con el servidor. La disponibilidad puede no estar al día; reintentando…",
  netBack: "Conexión restablecida.",
  noBest: (n: number) => `No hay ${n === 1 ? "una butaca disponible" : `${n} butacas contiguas disponibles`} con ese criterio. Prueba con menos o con otra categoría.`,
  bestDone: (n: number) => (n === 1 ? "Hemos elegido 1 butaca para ti." : `Hemos elegido ${n} butacas juntas para ti.`),
  selectedAnn: (label: string, count: number, total: string) =>
    `${label} seleccionada. ${count === 1 ? "1 entrada" : `${count} entradas`}${total ? `, total ${total}` : ""}.`,
  deselectedAnn: (label: string, count: number) => `${label} quitada. ${count === 0 ? "Sin selección" : count === 1 ? "1 entrada" : `${count} entradas`}.`,
  notAvailableAnn: (label: string) => `${label} no está disponible.`,
  gaSetAnn: (label: string, qty: number) => `${label}: ${qty === 1 ? "1 entrada" : `${qty} entradas`} en tu selección.`,
  gaRemovedAnn: (label: string) => `${label} quitada de tu selección.`,
  dismiss: "Cerrar aviso",
  reconnecting: "Reconectando…",
  total: "Total",
  nodeNone: "Sin selección",
};

type Messages = typeof es;

const en: Messages = {
  loading: "Loading the seat map…",
  loadError: "We couldn't load the seat map. Check your connection and try again.",
  authError: "The seat map couldn't be authorised. Please let the event organiser know.",
  notFoundError: "We couldn't find this event.",
  retry: "Try again",
  map: "Seat map",
  mapHelp: "Use the arrow keys to move between seats, Enter or Space to pick one, and plus or minus to zoom.",
  categories: "Categories and prices",
  highlight: (label) => `Highlight ${label} on the map`,
  legendFree: "Available",
  legendMine: "Your selection",
  legendSold: "Taken",
  legendHeld: "Being purchased",
  legendBlocked: "Unavailable",
  legendAccessible: "Accessible",
  legendTitle: "Key",
  stFree: "available",
  stMine: "selected",
  stSold: "taken",
  stHeld: "temporarily held by someone else",
  stBlocked: "unavailable",
  accessible: "accessible",
  seatTip: (section, row, seat) => `${section} · Row ${row} · Seat ${seat}`,
  seatAria: (section, row, seat, rest) => [section, `row ${row}`, `seat ${seat}`, ...rest].filter(Boolean).join(", "),
  zoneFree: (n) => `${n} left`,
  zoneSoldOut: "Sold out",
  zoneAria: (label, cat, price, free, mine) =>
    [label, cat, price && `${price} each`, free > 0 ? `${free} left` : "sold out", mine > 0 ? `${mine} selected` : ""].filter(Boolean).join(", "),
  zoneEach: "each",
  qty: "Quantity",
  qtyDec: "Fewer",
  qtyInc: "More",
  zoneAdd: (n) => (n === 1 ? "Add 1 ticket" : `Add ${n} tickets`),
  zoneUpdate: (n) => (n === 1 ? "Update to 1 ticket" : `Update to ${n} tickets`),
  zoneRemove: "Remove from my selection",
  close: "Close",
  zoneLimit: (n) => `Up to ${n} in this area`,
  zoomIn: "Zoom in",
  zoomOut: "Zoom out",
  zoomReset: "Reset view",
  hintWheel: (mod) => `Use ${mod} + scroll to zoom`,
  hintTouch: "Double-tap or pinch to zoom",
  barEmpty: (max) => `Pick your seats on the map (up to ${max}).`,
  tickets: (n) => (n === 1 ? "1 ticket" : `${n} tickets`),
  removeChip: (label) => `Remove ${label}`,
  holdTime: "Held for",
  extend: "Extend",
  extending: "Extending…",
  syncing: "Reserving…",
  minuteLeft: "You have less than a minute left to complete your purchase.",
  extended: (clock) => `We've extended your time. ${clock} left.`,
  extendMax: "The time can't be extended any further. Please complete your purchase now.",
  extendFailed: "We couldn't extend your time. Please try again.",
  holdExpired: "Your temporary hold has expired and the seats were released. Please pick them again.",
  holdBooked: "Your purchase has been confirmed.",
  maxReached: (n) => (n === 1 ? "You can only pick 1 ticket." : `You can pick up to ${n} tickets per purchase.`),
  seatLost: (label) => `${label} is no longer available and was removed from your selection.`,
  seatsLost: (n) => `${n} seats are no longer available and were removed from your selection.`,
  capacityLost: (zone) => `There aren't that many tickets left in ${zone}. We adjusted your selection.`,
  saveError: "We couldn't reserve your selection. Please try again.",
  removeError: "We couldn't remove the seat. It stays reserved.",
  zonesLimit: "You can't combine that many standing areas in one purchase.",
  quotaSession: (n) => `You've reached the maximum of ${n === 1 ? "1 seat" : n > 1 ? `${n} seats` : "seats"} held at once. Complete your purchase or release some to continue.`,
  quotaIp: (n) => `Too many seats are being held from your connection at once (limit ${n}). Release some, or try again when the holds expire.`,
  recovered: "We've restored your selection.",
  sessionRequired: "This sale requires you to sign in to the portal.",
  sessionInvalid: "Your purchase session has expired. Reload the page to continue.",
  netLost: "No connection to the server. Availability may be out of date; retrying…",
  netBack: "Connection restored.",
  noBest: (n) => `There ${n === 1 ? "isn't a seat" : `aren't ${n} adjacent seats`} available for that choice. Try fewer, or another category.`,
  bestDone: (n) => (n === 1 ? "We picked 1 seat for you." : `We picked ${n} seats together for you.`),
  selectedAnn: (label, count, total) => `${label} selected. ${count === 1 ? "1 ticket" : `${count} tickets`}${total ? `, total ${total}` : ""}.`,
  deselectedAnn: (label, count) => `${label} removed. ${count === 0 ? "Nothing selected" : count === 1 ? "1 ticket" : `${count} tickets`}.`,
  notAvailableAnn: (label) => `${label} is not available.`,
  gaSetAnn: (label, qty) => `${label}: ${qty === 1 ? "1 ticket" : `${qty} tickets`} in your selection.`,
  gaRemovedAnn: (label) => `${label} removed from your selection.`,
  dismiss: "Dismiss message",
  reconnecting: "Reconnecting…",
  total: "Total",
  nodeNone: "Nothing selected",
};

export const MESSAGES: Record<Locale, Messages> = { es, en };
export type { Messages };

export function messagesFor(locale: Locale | undefined): Messages {
  return MESSAGES[locale === "en" ? "en" : "es"];
}

/**
 * Aviso para los errores de cuota y de sesión (los que el widget no puede arreglar solo), o null si `code` es otro.
 * Usa `details.limit` / `details.scope` de `quota_exceeded`; sin límite legible el texto no lleva cifra.
 */
export function blockNotice(t: Messages, code: string, details?: unknown): string | null {
  if (code === "session_required") return t.sessionRequired;
  if (code === "session_invalid") return t.sessionInvalid;
  if (code !== "quota_exceeded") return null;
  const d = (details ?? {}) as { scope?: unknown; limit?: unknown };
  const n = typeof d.limit === "number" && Number.isFinite(d.limit) && d.limit >= 1 ? Math.floor(d.limit) : 0;
  return n > 0 && d.scope === "ip" ? t.quotaIp(n) : t.quotaSession(n);
}

export const normalizeLocale = (l: unknown): Locale => (l === "en" ? "en" : "es");
